<script setup lang="ts">
  import { computed } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { ColoredButton, IconImage } from '@/shared/ui';
  import CloseIcon from '@/shared/assets/icons/CloseIcon.svg';
  import { FilterEmits } from '@/shared/lib';

  interface ButtonsText {
    clear: string;
    apply: string;
    cancel?: string;
  }

  interface Props {
    contentClass?: string;
    buttonsText?: ButtonsText;
    replaceClearWithCancel?: boolean;
    applyDisabled?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    contentClass: 'w-3/4 max-w-2xl',
    buttonsText: () => ({
      clear: 'Limpar filtros',
      apply: 'Aplicar filtros',
    }),
    replaceClearWithCancel: false,
    applyDisabled: false,
  });

  const emit = defineEmits<FilterEmits>();

  const leftButtonLabel = computed(() =>
    props.replaceClearWithCancel
      ? (props.buttonsText.cancel ?? 'Cancelar')
      : props.buttonsText.clear
  );

  const onLeftButtonClick = () => {
    if (props.replaceClearWithCancel) {
      emit('close');
    } else {
      emit('clear-filters');
    }
  };
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    :content-class="`flex flex-col bg-white-01 px-3 rounded-2xl ${contentClass} max-h-[90vh] divide-y divide-gray-02`"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
  >
    <div class="flex justify-between items-center py-4">
      <h3 class="text-xl font-semibold text-gray-07">
        Filtros
      </h3>
      <button
        class="text-gray-05"
        @click="$emit('close')"
      >
        <IconImage
          :src="CloseIcon"
          alt="Close"
          class="cursor-pointer"
        />
      </button>
    </div>

    <slot name="content" />

    <div class="flex justify-end items-center gap-4 py-5 px-3">
      <ColoredButton
        :text="leftButtonLabel"
        :outlined="true"
        color="gray-03"
        border-color="gray-03"
        data-testid="filter-modal-left-action"
        @click="onLeftButtonClick"
      />
      <ColoredButton
        :text="buttonsText.apply"
        :disabled="applyDisabled"
        data-testid="filter-modal-apply"
        @click="$emit('apply-filters')"
      />
    </div>
  </VueFinalModal>
</template>
